import { useState } from "react";
import { Check, ChevronDown, ListChecks } from "lucide-react";
import {
  EMPTY_STAGE_REQUIRED_FIELDS,
  OPPORTUNITY_STANDARD_REQUIRED_FIELDS,
  OPPORTUNITY_STANDARD_REQUIRED_FIELD_LABELS,
  type OpportunityStageRequiredFields,
  type OpportunityStandardRequiredField,
} from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/** Another stage's name and requirements, used to hint where a field is also required. */
export interface OtherStageRequirements {
  name: string;
  requiredFields: OpportunityStageRequiredFields;
}

/** Props for the StageRequiredFieldsPicker component. */
export interface StageRequiredFieldsPickerProps {
  value: OpportunityStageRequiredFields;
  customFieldNames: string[];
  stageName: string;
  otherStages: OtherStageRequirements[];
  onChange: (value: OpportunityStageRequiredFields) => void;
  disabled?: boolean;
}

/** A field option listed in the picker. */
interface FieldOption {
  key: string;
  label: string;
  isRequired: boolean;
  alsoRequiredAt: string[];
  toggle: () => void;
}

/**
 * Multi-select popover for the standard and custom fields an opportunity must
 * have filled to enter a stage. Custom names no longer in the schema stay in
 * the value but are not listed or counted.
 * @param props - StageRequiredFieldsPickerProps
 * @returns The required fields trigger and popover
 */
export function StageRequiredFieldsPicker({
  value,
  customFieldNames,
  stageName,
  otherStages,
  onChange,
  disabled = false,
}: StageRequiredFieldsPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [hasToggled, setHasToggled] = useState(false);

  const liveCustom = value.custom.filter((name) =>
    customFieldNames.includes(name),
  );
  const requiredLabels = [
    ...value.standard.map(
      (field) => OPPORTUNITY_STANDARD_REQUIRED_FIELD_LABELS[field],
    ),
    ...liveCustom,
  ];
  const requiredCount = requiredLabels.length;
  const query = search.trim().toLowerCase();

  const change = (next: OpportunityStageRequiredFields) => {
    setHasToggled(true);
    onChange(next);
  };

  const toggleStandard = (field: OpportunityStandardRequiredField) => {
    const isRequired = value.standard.includes(field);
    change({
      ...value,
      standard: OPPORTUNITY_STANDARD_REQUIRED_FIELDS.filter((candidate) => {
        if (candidate === field) {
          return !isRequired;
        }
        return value.standard.includes(candidate);
      }),
    });
  };

  const toggleCustom = (name: string) => {
    if (value.custom.includes(name)) {
      change({ ...value, custom: value.custom.filter((n) => n !== name) });
      return;
    }
    change({ ...value, custom: [...value.custom, name] });
  };

  const matchesSearch = (option: FieldOption) =>
    option.label.toLowerCase().includes(query);

  const standardOptions: FieldOption[] =
    OPPORTUNITY_STANDARD_REQUIRED_FIELDS.map((field) => ({
      key: `standard:${field}`,
      label: OPPORTUNITY_STANDARD_REQUIRED_FIELD_LABELS[field],
      isRequired: value.standard.includes(field),
      alsoRequiredAt: otherStages
        .filter((stage) => stage.requiredFields.standard.includes(field))
        .map((stage) => stage.name),
      toggle: () => toggleStandard(field),
    })).filter(matchesSearch);

  const customOptions: FieldOption[] = customFieldNames
    .map((name) => ({
      key: `custom:${name}`,
      label: name,
      isRequired: value.custom.includes(name),
      alsoRequiredAt: otherStages
        .filter((stage) => stage.requiredFields.custom.includes(name))
        .map((stage) => stage.name),
      toggle: () => toggleCustom(name),
    }))
    .filter(matchesSearch);

  const renderOption = (option: FieldOption) => (
    <CommandItem
      key={option.key}
      value={option.key}
      onSelect={option.toggle}
      aria-checked={option.isRequired}
      data-checked={option.isRequired}
      className="group items-start gap-2 text-[13px]"
    >
      <span className="mt-px grid size-4 shrink-0 place-items-center rounded-[4px] border border-foreground/35 transition-colors group-data-[checked=true]:border-foreground group-data-[checked=true]:bg-foreground group-data-[checked=true]:text-background">
        <Check className="!size-3 [stroke-dasharray:24] [stroke-dashoffset:24] group-data-[checked=true]:[stroke-dashoffset:0] motion-safe:transition-[stroke-dashoffset] motion-safe:duration-200 motion-safe:ease-out" />
      </span>
      <span className="min-w-0 [overflow-wrap:anywhere]">
        {option.label}
        {option.alsoRequiredAt.length > 0 && (
          <span className="mt-px block text-[11px] text-muted-foreground">
            Also required at {option.alsoRequiredAt.join(", ")}
          </span>
        )}
      </span>
    </CommandItem>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSearch("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          title={requiredLabels.join(", ") || undefined}
          className={cn(
            "h-8 w-full justify-start gap-1.5 px-2.5 text-[13px] font-normal [&_svg]:size-3.5",
            requiredCount > 0 && "border-foreground/25",
          )}
        >
          <ListChecks className="text-muted-foreground" />
          {requiredCount > 0 ? (
            <>
              <span className="flex-1 text-left">Required</span>
              <span
                key={requiredCount}
                className={cn(
                  "inline-grid h-[18px] min-w-5 place-items-center rounded-full bg-foreground px-[5px] text-[11px] font-semibold text-background",
                  hasToggled &&
                    "motion-safe:animate-in motion-safe:zoom-in-[1.18] motion-safe:duration-200 motion-safe:ease-out",
                )}
              >
                {requiredCount}
              </span>
            </>
          ) : (
            <span className="flex-1 text-left text-muted-foreground">
              No required fields
            </span>
          )}
          <ChevronDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[340px] max-w-[calc(100vw-32px)] p-1.5"
      >
        <Command
          shouldFilter={false}
          aria-label={`Required fields for ${stageName || "this stage"}`}
        >
          <CommandInput
            placeholder="Search fields"
            value={search}
            onValueChange={setSearch}
            className="h-8 text-[13px]"
          />
          <CommandList className="mt-1">
            {standardOptions.length > 0 && (
              <CommandGroup heading="Standard fields">
                {standardOptions.map(renderOption)}
              </CommandGroup>
            )}
            {customOptions.length > 0 && (
              <CommandGroup heading="Custom fields">
                {customOptions.map(renderOption)}
              </CommandGroup>
            )}
            {standardOptions.length === 0 && customOptions.length === 0 && (
              <p className="px-2 py-3 text-xs text-muted-foreground">
                No field matches "{search.trim()}"
              </p>
            )}
          </CommandList>
          <div className="mt-1 flex items-center justify-between gap-2 border-t pb-0.5 pl-2 pr-1 pt-1.5 text-xs text-muted-foreground">
            <span className="min-w-0 [overflow-wrap:anywhere]">
              Required to enter {stageName || "this stage"}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={requiredCount === 0}
              onClick={() => change(EMPTY_STAGE_REQUIRED_FIELDS)}
              className="h-[26px] shrink-0 px-2 text-xs"
            >
              Clear
            </Button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
